import React, { Dispatch, SetStateAction } from 'react';
import { Adaptive, TabList, Tab } from 'fronton-react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Controller, useFormContext } from 'react-hook-form';
import { activeRoomId, roomArea } from 'pages/common/store/rooms';
import { LayingTypeField } from 'pages/common/components/LayingTypeField';
import { TInputParams, ParametersEnum } from 'pages/common/types';
import { getAreaState } from 'pages/common/helpers/get-area';
import { AdaptiveInput } from 'pages/common/components/AdaptiveInput';
import { TAB_LIST, tabListLabels } from 'pages/common/consts/dimensions-tab-list';
import { useIsNeedToResetActiveRoom } from 'pages/common/hooks/use-is-need-to-reset-active-room';
import { roomParametersValidation } from 'pages/common/helpers/room-parameters-validation';
import { dimensionsErrors, dimensionsLabels } from '../../../dimensions/const';
import { ButtonContainer, InputContainerColumn, InputContainerRow, styledTabList } from './styles';
import { RoomTypeField } from './room-type-field/room-type-field';

const parameters = {
  [TAB_LIST.AREA]: [ParametersEnum.area],
  [TAB_LIST.LENGTH_WIDTH]: [ParametersEnum.length, ParametersEnum.width],
};
const defaultInputParams: TInputParams = [ParametersEnum.length, ParametersEnum.width];

type TProps = {
  setInputParams: Dispatch<SetStateAction<TInputParams>>;
  activeTabIndex: number;
  setActiveTabIndex: Dispatch<SetStateAction<number>>;
};

export const RoomsParams = ({ setInputParams, setActiveTabIndex, activeTabIndex }: TProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [isAreaEnabled, setIsAreaEnabled] = useRecoilState(roomArea);
  const setSelectedRoomId = useSetRecoilState(activeRoomId);
  const { isNeedToResetActiveRoom } = useIsNeedToResetActiveRoom();

  const activeTabParameters = parameters[activeTabIndex];

  const changeInputsParams = (isEnabled: boolean) => {
    const areaState = getAreaState({
      inputParams: defaultInputParams,
      isEnabled,
    });

    setInputParams(areaState);
  };

  const onAreaButtonClick = (value: string) => {
    const areaEnabledState = !isAreaEnabled;
    setIsAreaEnabled(areaEnabledState);
    setActiveTabIndex(Number(value));
    if (isNeedToResetActiveRoom) {
      setSelectedRoomId('');
    }
    changeInputsParams(areaEnabledState);
  };

  const handleUnselectRoom = () => {
    setSelectedRoomId('');
  };

  const handleChange = (onChange: (value: string) => void) => (value: string) => {
    handleUnselectRoom();
    onChange(value);
  };

  return (
    <>
      <div>
        <RoomTypeField />
      </div>
      <div>
        <ButtonContainer>
          <TabList
            size="s"
            active={activeTabIndex}
            onChangeTab={event => {
              onAreaButtonClick((event.target as HTMLInputElement).value);
            }}
            className={styledTabList}>
            {tabListLabels.map((el, index) => (
              <Tab key={el.id} id={String(index)} value={index} data-testid={`tab-${el.id}`}>
                {el.label}
              </Tab>
            ))}
          </TabList>
        </ButtonContainer>
        <InputContainerRow>
          {activeTabParameters.map(parameter => (
            <InputContainerColumn key={parameter}>
              <Controller
                name={parameter}
                control={control}
                rules={{
                  validate: {
                    required: value => roomParametersValidation(value, parameter),
                  },
                  pattern: {
                    value: dimensionsErrors[parameter]?.value,
                    message: dimensionsErrors[parameter]?.pattern,
                  },
                }}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Adaptive to="tablet">
                      <AdaptiveInput
                        form={{
                          value,
                          onChange: handleChange(onChange),
                          errors,
                        }}
                        name={parameter}
                        locale={{ labels: dimensionsLabels }}
                        mobile
                      />
                    </Adaptive>
                    <Adaptive from="tablet">
                      <AdaptiveInput
                        form={{
                          value,
                          onChange: handleChange(onChange),
                          errors,
                        }}
                        name={parameter}
                        locale={{ labels: dimensionsLabels }}
                        mobile={false}
                      />
                    </Adaptive>
                  </>
                )}
              />
            </InputContainerColumn>
          ))}
        </InputContainerRow>
      </div>
      <div>
        <LayingTypeField fieldName="layingType" onChangeValue={handleUnselectRoom} />
      </div>
    </>
  );
};
